import { RoleDTO, UserDTO } from '@/models';
import { roleService } from '@/services/api/roleService';
import { userService } from '@/services/api/userService';
import { usePermissionStore, useUIStore } from '@/store';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

export interface PermissionRow {
  id: string;
  name: string;
  key: string | null;
}

export interface PermissionGroup {
  prefix: string;
  permissions: PermissionRow[];
}

export const useUserPermissionManagement = () => {
  const { institutionId, userId } = useParams<{ institutionId: string; userId: string }>();

  const {
    userPermissions,
    isLoading: isPermissionLoading,
    error,
    fetchUserPermissions,
    replaceUserPermissions,
    clearError,
  } = usePermissionStore();
  const { addNotification } = useUIStore();

  const [user, setUser] = useState<UserDTO | null>(null);
  const [allRoles, setAllRoles] = useState<RoleDTO[]>([]);
  const [isLoadingEntities, setIsLoadingEntities] = useState(true);

  const [selectedPermissionIds, setSelectedPermissionIds] = useState<Set<string>>(new Set());
  const [initialPermissionIds, setInitialPermissionIds] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  // Load user info and all roles
  useEffect(() => {
    if (!institutionId || !userId) return;

    const loadData = async () => {
      setIsLoadingEntities(true);
      try {
        const [userRes, rolesRes] = await Promise.all([
          userService.getById(institutionId, userId),
          roleService.getAll(),
        ]);
        setUser(userRes);
        setAllRoles(rolesRes);
      } catch {
        // Errors handled by services
      } finally {
        setIsLoadingEntities(false);
      }
    };

    loadData();
  }, [institutionId, userId]);

  // Load current user permissions
  useEffect(() => {
    if (!userId || !user?.institutionId) return;
    fetchUserPermissions(user.institutionId, userId);
  }, [userId, user?.institutionId, fetchUserPermissions]);

  // Sync store permissions to local state
  useEffect(() => {
    if (!userPermissions) return;
    const ids = new Set(userPermissions.map((p) => p.id));
    setSelectedPermissionIds(ids);
    setInitialPermissionIds(ids);
  }, [userPermissions]);

  // Roles that have permissions
  const rolesWithPermissions = useMemo(
    () => allRoles.filter((r) => r.permissions && r.permissions.length > 0),
    [allRoles]
  );

  // Deduplicated union of all permissions across all roles, grouped by key prefix
  const allPermissionRows = useMemo((): PermissionGroup[] => {
    const seen = new Map<string, PermissionRow>();
    for (const role of rolesWithPermissions) {
      for (const p of role.permissions ?? []) {
        if (!seen.has(p.id)) {
          seen.set(p.id, { id: p.id, name: p.name ?? p.key ?? p.id, key: p.key ?? null });
        }
      }
    }
    const groupMap = new Map<string, PermissionRow[]>();
    for (const perm of seen.values()) {
      const prefix = perm.key?.split('.')[0] ?? 'Other';
      if (!groupMap.has(prefix)) groupMap.set(prefix, []);
      groupMap.get(prefix)!.push(perm);
    }
    return Array.from(groupMap.entries())
      .map(([prefix, permissions]) => ({
        prefix,
        permissions: permissions.sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => {
        if (a.prefix === 'FullAccess') return -1;
        if (b.prefix === 'FullAccess') return 1;
        return a.prefix.localeCompare(b.prefix);
      });
  }, [rolesWithPermissions]);

  // roleId → Set of permissionIds that role contains
  const rolePermissionMap = useMemo((): Map<string, Set<string>> => {
    const map = new Map<string, Set<string>>();
    for (const role of rolesWithPermissions) {
      map.set(role.id, new Set((role.permissions ?? []).map((p) => p.id)));
    }
    return map;
  }, [rolesWithPermissions]);

  const togglePermission = useCallback((permissionId: string) => {
    setSelectedPermissionIds((prev) => {
      const next = new Set(prev);
      if (next.has(permissionId)) {
        next.delete(permissionId);
      } else {
        next.add(permissionId);
      }
      return next;
    });
  }, []);

  const selectAllForRole = useCallback(
    (roleId: string, selectAll: boolean) => {
      const rolePermIds = rolePermissionMap.get(roleId);
      if (!rolePermIds) return;
      setSelectedPermissionIds((prev) => {
        const next = new Set(prev);
        for (const id of rolePermIds) {
          if (selectAll) next.add(id);
          else next.delete(id);
        }
        return next;
      });
    },
    [rolePermissionMap]
  );

  const replaceWithRole = useCallback(
    (roleId: string) => {
      const rolePermIds = rolePermissionMap.get(roleId);
      if (!rolePermIds) return;
      setSelectedPermissionIds(new Set(rolePermIds));
    },
    [rolePermissionMap]
  );

  const setFullAccess = useCallback(
    (enabled: boolean) => {
      const fullAccessGroup = allPermissionRows.find((g) => g.prefix === 'FullAccess');
      const fullAccessIds = fullAccessGroup?.permissions.map((p) => p.id) ?? [];
      if (enabled) {
        setSelectedPermissionIds(new Set(fullAccessIds));
      } else {
        setSelectedPermissionIds((prev) => {
          const next = new Set(prev);
          for (const id of fullAccessIds) next.delete(id);
          return next;
        });
      }
    },
    [allPermissionRows]
  );

  // Dirty state
  const isDirty = useMemo(() => {
    if (initialPermissionIds.size === 0 && selectedPermissionIds.size === 0) return false;
    if (initialPermissionIds.size !== selectedPermissionIds.size) return true;
    return ![...selectedPermissionIds].every((id) => initialPermissionIds.has(id));
  }, [initialPermissionIds, selectedPermissionIds]);

  // Save handler
  const handleSave = useCallback(async () => {
    if (!userId || !user?.institutionId) return;
    setIsSaving(true);
    try {
      await replaceUserPermissions(user.institutionId, userId, Array.from(selectedPermissionIds));
      addNotification({
        type: 'success',
        message: 'User permissions updated successfully',
        duration: 2000,
      });
    } catch (err: unknown) {
      addNotification({
        type: 'error',
        message: (err as Error)?.message || 'Operation failed',
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  }, [userId, user?.institutionId, selectedPermissionIds, replaceUserPermissions, addNotification]);

  const isLoading = isPermissionLoading || isLoadingEntities;

  return {
    user,
    isLoading,
    isSaving,
    error,
    isDirty,
    rolesWithPermissions,
    allPermissionRows,
    rolePermissionMap,
    selectedPermissionIds,
    togglePermission,
    selectAllForRole,
    replaceWithRole,
    setFullAccess,
    handleSave,
    clearError,
  };
};
